"use client"

import { createRef } from "react"
import clsx from "clsx"
import { useHover } from "usehooks-ts"

import { Accordion, AccordionPanel } from "@unlikelystudio/react-abstract-components"

import type { InferReturn, PropsWithClassName } from "~/@types/generics"
import { AccordionPanelHead } from "~/components/ui/AccordionPanelHead"
import { title as accordionPanelHeadTitleStyle } from "~/components/ui/AccordionPanelHead/styles.css"
import HtmlRichText from "~/components/ui/HtmlRichText"
import InlineCta from "~/components/ui/InlineCta"
import { Link } from "~/components/ui/Link"
import {
  TRACEABILITY_KEY,
  type serializeProductAccordions,
} from "~/components/ui/ProductHeader/components/ProductDetails/components/ProductDetailsAccordions/_data/serializer"
import RichText from "~/components/ui/RichText"
import Icon from "~/components/abstracts/Icon"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import * as css from "./styles.css"

type PanelProps = ProductDetailsAccordionsProps["items"][number] & { index: number }

export type ProductDetailsAccordionsProps = PropsWithClassName<InferReturn<typeof serializeProductAccordions>>

function PanelBody({ content }: { content: PanelProps["body"] }) {
  const t = useTranslate()

  if (!content) return null

  if ("description" in content) {
    const { description, productRef } = content
    const descriptionWithoutDash = description?.replace(/<li>\s*-[^<]*<\/li>/gm, (match) => match.replace("-", ""))

    return (
      <>
        {description && <HtmlRichText className={css.content}>{descriptionWithoutDash}</HtmlRichText>}
        {productRef && <div className={css.productRef}>{`${t("description_ref_title")} : ${productRef}`}</div>}
      </>
    )
  }

  if ("href" in content) {
    return <InlineCta className={css.content} {...content} />
  }

  if ("type" in content) {
    content
    return <RichText className={css.content} render={content} />
  }

  return null
}

export function ProductDetailsAccordions({ className, items }: ProductDetailsAccordionsProps) {
  const headRef = createRef<HTMLAnchorElement | HTMLButtonElement>()
  const isHover = useHover(headRef)
  return (
    <Accordion className={clsx(css.ProductDetailsAccordions, className)} autoHide={true} index={0}>
      {items
        .filter((item) => item?.head || item?.body)
        .map(({ head, body }, index) => {
          const isTraceability = "href" in body && body?.children === TRACEABILITY_KEY

          if (isTraceability) {
            return (
              <Link ref={headRef} href={body.href} key={`panel_${index}`} className={css.panel} target="_blank">
                <div className={css.head}>
                  <InlineCta active={isHover} withLine className={accordionPanelHeadTitleStyle}>
                    {head}
                  </InlineCta>
                  <Icon name="Arrow" width={12} direction="right" />
                </div>
              </Link>
            )
          }

          return (
            <AccordionPanel
              key={`panel_${index}`}
              className={css.panel}
              headClassName={css.head}
              index={index}
              ariaLabels={{ close: head, open: head }}
              head={(isActive: boolean) => <AccordionPanelHead title={head} isActive={isActive} />}
              body={
                isTraceability ? null : (
                  <div className={css.body}>
                    <PanelBody content={body} />
                  </div>
                )
              }
            />
          )
        })}
    </Accordion>
  )
}
